import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { 
  User, 
  Users, 
  Database, 
  HardDrive, 
  Server, 
  Cloud, 
  Monitor, 
  Smartphone, 
  Globe, 
  Shield, 
  Key, 
  Cpu, 
  MemoryStick, 
  Wifi, 
  Router, 
  Layers, 
  Package, 
  Settings, 
  Activity, 
  BarChart3,
  X 
} from 'lucide-react';
import styles from './IconPalette.module.css';

const SOLUTION_DESIGN_ICONS = [
  { name: 'User', icon: User, label: 'User' },
  { name: 'Users', icon: Users, label: 'Users' },
  { name: 'Database', icon: Database, label: 'Database' },
  { name: 'HardDrive', icon: HardDrive, label: 'Storage' },
  { name: 'Server', icon: Server, label: 'Server' },
  { name: 'Cloud', icon: Cloud, label: 'Cloud' },
  { name: 'Monitor', icon: Monitor, label: 'Desktop' },
  { name: 'Smartphone', icon: Smartphone, label: 'Mobile' },
  { name: 'Globe', icon: Globe, label: 'Internet' },
  { name: 'Shield', icon: Shield, label: 'Security' },
  { name: 'Key', icon: Key, label: 'Authentication' },
  { name: 'Cpu', icon: Cpu, label: 'Processor' },
  { name: 'MemoryStick', icon: MemoryStick, label: 'Memory' },
  { name: 'Wifi', icon: Wifi, label: 'Network' },
  { name: 'Router', icon: Router, label: 'Load Balancer' },
  { name: 'Layers', icon: Layers, label: 'Layers' },
  { name: 'Package', icon: Package, label: 'Package' },
  { name: 'Settings', icon: Settings, label: 'Configuration' },
  { name: 'Activity', icon: Activity, label: 'Monitoring' },
  { name: 'BarChart3', icon: BarChart3, label: 'Analytics' },
];

interface IconPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export const IconPalette: React.FC<IconPaletteProps> = observer(({ isOpen, onClose }) => {
  const [draggedIcon, setDraggedIcon] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleDragStart = (e: React.DragEvent, iconName: string) => {
    setDraggedIcon(iconName);
    
    // Set the drag data
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'icon',
      iconName,
      width: 48,
      height: 48,
    }));
    e.dataTransfer.effectAllowed = 'copy';
    
    // Create a custom drag image (optional)
    const dragImage = e.currentTarget.cloneNode(true) as HTMLElement;
    dragImage.style.transform = 'rotate(5deg)';
    dragImage.style.opacity = '0.8';
    e.dataTransfer.setDragImage(dragImage, 32, 32);
  };

  const handleDragEnd = () => {
    setDraggedIcon(null);
  };

  return (
    <div className={styles['iconPalette']}>
      <div className={styles['header']}>
        <h3>Solution Design Icons</h3>
        <button 
          className={styles['closeButton']} 
          onClick={onClose}
          aria-label="Close icon palette"
        >
          <X size={16} />
        </button>
      </div>
      <div className={styles['iconGrid']}>
        {SOLUTION_DESIGN_ICONS.map(({ name, icon: IconComponent, label }) => (
          <div
            key={name}
            className={`${styles['iconItem']} ${draggedIcon === name ? styles['dragging'] : ''}`}
            draggable
            onDragStart={(e) => handleDragStart(e, name)}
            onDragEnd={handleDragEnd}
            title={label}
          >
            <IconComponent size={24} />
            <span className={styles['iconLabel']}>{label}</span>
          </div>
        ))}
      </div>
      <div className={styles['instructions']}>
        Drag icons to the canvas to add them to your diagram
      </div>
    </div>
  );
}); 